import * as React from 'react'

import {
  CONTENT_WRAPPER_CLASS_NAME,
  CUSTOM_EVENT_SCROLL_TO_HIGHLIGHT,
  EDocumentComparisonChangeType,
  ETargetVersion,
  FOCUSED_HIGHLIGHT_CLASS_NAME,
  FOCUSED_HIGHLIGHT_CONTENT,
} from '../../constants'
import { useGetFileContentByBlobURL, useZoomControl } from '../../hooks'
import { useVersionComparisonStore } from '../../store'
import { THighlightEventDetail, TSelectedVersion } from '../../types'
import { IHTMLCompareChangeItem } from '../types'
import { applySourceDiffHighlights } from '../utils'

/**
 * Options for configuring the HTML controller hook
 */
type UseHtmlControllerHookOptions = {
  signedUrl?: string // Signed URL to fetch HTML content from
  version: TSelectedVersion // Current version being viewed
  allVersionsReady: boolean // Whether all versions are ready for sync operations
  compareDocumentDiff: IHTMLCompareChangeItem[] | null // Diff between versions to highlight
  scrollContainerRef: React.RefObject<HTMLDivElement | null> // Ref to scroll container for scroll-to-highlight
  isEnabled: boolean // Whether this controller is active
}

/**
 * Return type for the HTML controller hook
 * Includes shadow DOM refs, content, loading state, and zoom controls
 */
type UseHtmlControllerHookResult = {
  shadowHostRef: React.RefObject<HTMLDivElement> // Ref to shadow host element
  shadowRootRef: React.RefObject<ShadowRoot | null> // Ref to shadow root
  content: string | undefined // HTML content
  isLoading: boolean // Whether content is loading
  error: Error | null // Error loading content, if any
  zoomIn: () => void // Increase zoom level
  zoomOut: () => void // Decrease zoom level
  canZoomIn: boolean // Whether zoom in is possible
  canZoomOut: boolean // Whether zoom out is possible
}

/**
 * Custom hook for managing HTML document viewing and interaction
 * Handles shadow DOM setup, content loading, highlight application, and zoom control
 * Coordinates with global version comparison store for synchronized features
 *
 * Features:
 * - Shadow DOM isolation for HTML content
 * - Highlights for version comparison changes
 * - Scroll-to-highlight synchronization across viewers
 * - Zoom controls with synchronized scaling
 *
 * @param options - Configuration options for the controller
 * @returns Object containing shadow DOM refs, content, and control methods
 */
export const useHtmlController = ({
  signedUrl,
  version,
  allVersionsReady,
  compareDocumentDiff,
  scrollContainerRef,
  isEnabled,
}: UseHtmlControllerHookOptions): UseHtmlControllerHookResult => {
  const { documentId, setVersionReady } = useVersionComparisonStore()
  const versionId = version?.id ?? ''

  const {
    data: content,
    isLoading,
    error,
  } = useGetFileContentByBlobURL({
    signedUrl: isEnabled ? (signedUrl ?? '') : '',
    documentId: documentId ?? '',
    versionId,
  })

  const shadowHostRef = React.useRef<HTMLDivElement>(null)
  const shadowRootRef = React.useRef<ShadowRoot | null>(null)

  /**
   * Effect: Set up shadow DOM for HTML content
   * Creates shadow root and injects HTML content with proper styling
   * Marks version as ready when content is loaded
   */
  React.useEffect(() => {
    if (!isEnabled) {
      shadowRootRef.current = null
      return
    }

    const host = shadowHostRef.current
    if (!host) return

    // Create or get existing shadow root
    let shadowRoot = shadowRootRef.current
    if (shadowRoot?.host !== host) {
      shadowRoot = host.shadowRoot ?? host.attachShadow({ mode: 'open' })
      shadowRootRef.current = shadowRoot
    }

    if (!shadowRoot) return

    // Create style element for shadow DOM with proper CSS resets
    const style = document.createElement('style')
    style.textContent = `
      :host {
        all: initial;
        display: block;
        width: 100%;
        height: 100%;
      }

      :host, :host * {
        box-sizing: border-box;
      }
    `

    // Create content wrapper
    const contentWrapper = document.createElement('div')
    contentWrapper.className = CONTENT_WRAPPER_CLASS_NAME
    contentWrapper.innerHTML = content ?? ''

    // Replace children with style and content
    shadowRoot.replaceChildren(style, contentWrapper)

    // Mark version as ready when content is loaded
    if (versionId && content) {
      setVersionReady(versionId, true)
    }
  }, [content, isEnabled, setVersionReady, versionId, shadowHostRef.current])

  const ensureFocusedHighlightStyles = React.useCallback(
    (shadowRoot: ShadowRoot) => {
      const existingStyle = shadowRoot.querySelector(
        'style[data-focused-highlight]'
      )
      if (existingStyle) return

      const style = document.createElement('style')
      style.dataset.focusedHighlight = 'true'
      style.textContent = FOCUSED_HIGHLIGHT_CONTENT
      shadowRoot.appendChild(style)
    },
    []
  )

  const removeFocusedHighlight = React.useCallback((shadowRoot: ShadowRoot) => {
    shadowRoot
      .querySelectorAll(`.${FOCUSED_HIGHLIGHT_CLASS_NAME}`)
      .forEach((element) => {
        element.classList.remove(FOCUSED_HIGHLIGHT_CLASS_NAME)
      })
  }, [])

  const setFocusedHighlightByUuid = React.useCallback(
    (shadowRoot: ShadowRoot, uuid: string) => {
      ensureFocusedHighlightStyles(shadowRoot)

      removeFocusedHighlight(shadowRoot)

      const target = shadowRoot.querySelector(`[data-uuid="${uuid}"]`)
      if (target) target.classList.add(FOCUSED_HIGHLIGHT_CLASS_NAME)
    },
    [ensureFocusedHighlightStyles, removeFocusedHighlight]
  )

  /**
   * Effect: Apply highlights for version comparison
   * Applies delete highlights for source version, insert highlights for target version
   * Only runs when diff is available and version is ready
   */
  React.useEffect(() => {
    if (!isEnabled) return

    // Check if shadow root is ready
    const shadowRoot = shadowRootRef.current
    if (!shadowRoot) return

    // Check if comparison diff is available
    if (!compareDocumentDiff) return

    // Check if version is ready
    if (!allVersionsReady) return

    // Apply delete highlights for previous version
    if (version?.isSource) {
      applySourceDiffHighlights({
        shadowRoot,
        diffOps: compareDocumentDiff,
        isSource: true,
      })
    }

    // Apply insert highlights for current version
    if (version?.isTarget) {
      applySourceDiffHighlights({
        shadowRoot,
        diffOps: compareDocumentDiff,
        isTarget: true,
      })
    }
  }, [
    allVersionsReady,
    compareDocumentDiff,
    isEnabled,
    version?.isSource,
    version?.isTarget,
  ])

  /**
   * Effect: Listen for scroll-to-highlight events and scroll to highlighted element
   * Handles cross-viewer synchronization when user clicks a highlight in the sidebar
   * Calculates positions relative to the scroll container and smoothly scrolls to center the element
   */
  React.useEffect(() => {
    if (!isEnabled) return

    // Check if shadow root is ready
    const shadowRoot = shadowRootRef.current
    if (!shadowRoot) return

    // Check if scroll container is ready
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) return

    // Check if comparison diff is available
    if (!compareDocumentDiff) return

    // Check if version is ready
    if (!allVersionsReady) return

    const handleScrollToHighlight = (event: Event) => {
      const customEvent = event as CustomEvent<THighlightEventDetail>
      const { uuid, targetVersion, type } = customEvent.detail || {}

      if (!uuid || !targetVersion) return

      const isReplaceChange = type === EDocumentComparisonChangeType.REPLACE

      // Check if this Layout is the target version
      const isTargetVersion =
        (targetVersion === ETargetVersion.SOURCE && version?.isSource) ||
        (targetVersion === ETargetVersion.TARGET && version?.isTarget)

      // Replace change should focus the highlight
      if (isReplaceChange) {
        setFocusedHighlightByUuid(shadowRoot, uuid)
      } else {
        removeFocusedHighlight(shadowRoot)
      }

      if (!isTargetVersion) return

      // Find the highlighted element with the matching UUID
      const highlightedElement = shadowRoot.querySelector(
        `[data-uuid="${uuid}"]`
      ) as HTMLElement

      if (highlightedElement) {
        // Get bounding rects for position calculation
        const elementRect = highlightedElement.getBoundingClientRect()
        const shadowHost = shadowRoot.host
        const hostRect = shadowHost.getBoundingClientRect()
        const containerRect = scrollContainer.getBoundingClientRect()

        // Calculate element position relative to scroll container (vertical)
        const elementTopRelativeToHost = elementRect.top - hostRect.top
        const hostTopRelativeToContainer =
          hostRect.top - containerRect.top + scrollContainer.scrollTop

        // Calculate element position relative to scroll container (horizontal)
        const elementLeftRelativeToHost = elementRect.left - hostRect.left
        const hostLeftRelativeToContainer =
          hostRect.left - containerRect.left + scrollContainer.scrollLeft

        // Calculate target scroll position to center the element (vertical)
        const containerHeight = scrollContainer.clientHeight
        const elementHeight = elementRect.height
        const targetScrollTop =
          hostTopRelativeToContainer +
          elementTopRelativeToHost -
          containerHeight / 2 +
          elementHeight / 2

        // Calculate target scroll position to center the element (horizontal)
        const containerWidth = scrollContainer.clientWidth
        const elementWidth = elementRect.width
        const targetScrollLeft =
          hostLeftRelativeToContainer +
          elementLeftRelativeToHost -
          containerWidth / 2 +
          elementWidth / 2

        // Scroll the container smoothly (both vertical and horizontal)
        scrollContainer.scrollTo({
          top: Math.max(0, targetScrollTop),
          left: Math.max(0, targetScrollLeft),
          behavior: 'smooth',
        })

        setFocusedHighlightByUuid(shadowRoot, uuid)
      }
    }

    globalThis.addEventListener(
      CUSTOM_EVENT_SCROLL_TO_HIGHLIGHT,
      handleScrollToHighlight
    )

    return () => {
      globalThis.removeEventListener(
        CUSTOM_EVENT_SCROLL_TO_HIGHLIGHT,
        handleScrollToHighlight
      )
    }
  }, [
    allVersionsReady,
    compareDocumentDiff,
    isEnabled,
    removeFocusedHighlight,
    scrollContainerRef,
    setFocusedHighlightByUuid,
    version?.isSource,
    version?.isTarget,
  ])

  /**
   * Get zoom control functions using custom hook
   */
  const { zoomIn, zoomOut, canZoomIn, canZoomOut } = useZoomControl(
    shadowRootRef,
    allVersionsReady && isEnabled
  )

  /**
   * Memoize and return all control methods and state
   * This prevents unnecessary re-renders of child components that use this hook
   */
  return React.useMemo(
    () => ({
      shadowHostRef,
      shadowRootRef,
      content,
      isLoading,
      error,
      zoomIn,
      zoomOut,
      canZoomIn,
      canZoomOut,
    }),
    [canZoomIn, canZoomOut, content, error, isLoading, zoomIn, zoomOut]
  )
}
